import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gift, Users, Copy, Share2, Check, Trophy, Crown } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { notify } from '../components/BalmartToaster';
import TextScramble from '../components/TextScramble';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPage() {
  const { customer, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    if (!authLoading && !customer) navigate('/login');
  }, [authLoading, customer, navigate]);

  useEffect(() => {
    if (!customer) return;
    axios.get(`${API}/customer/referral`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData(null));
    axios.get(`${API}/referral/leaderboard`)
      .then(r => setLeaderboard(r.data?.top || []))
      .catch(() => setLeaderboard([]));
  }, [customer]);

  const copy = (value, label) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value);
      setCopied(label);
      notify.success(`${label} copié`);
      setTimeout(() => setCopied(''), 2000);
    }
  };

  const share = async (platform) => {
    if (!data) return;
    const msg = `Rejoins-moi sur Balmart ! Profite de -${data.discount_percent}% sur ta première commande avec mon code ${data.code} · ${data.share_url}`;
    if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(data.share_url)}`, '_blank');
    } else if (platform === 'x') {
      // X (ex-Twitter) intent URL
      window.open(`https://x.com/intent/post?text=${encodeURIComponent(msg)}`, '_blank');
    } else if (platform === 'instagram') {
      // Instagram n'a pas d'intent web — on copie le lien + on ouvre l'app
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(msg);
        notify.success('Lien copié · collez-le dans votre story ou DM Instagram');
      }
      window.open('https://www.instagram.com/', '_blank');
    } else if (platform === 'tiktok') {
      // TikTok n'a pas d'intent web — on copie le lien + on ouvre TikTok
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(msg);
        notify.success('Lien copié · collez-le dans votre bio ou message TikTok');
      }
      window.open('https://www.tiktok.com/', '_blank');
    } else if (platform === 'native' && typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'Balmart', text: msg, url: data.share_url });
      } catch {}
    }
  };

  if (authLoading || !customer) {
    return <div className="min-h-screen flex items-center justify-center"><p className="text-black/30 text-sm">Chargement...</p></div>;
  }

  return (
    <div data-testid="referral-page" className="pt-24 sm:pt-28 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16">
          <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-black/40 mb-3 flex items-center gap-2">
            <Gift size={12} fill="#B80000" stroke="#B80000" /> Programme Parrainage
          </p>
          <TextScramble
            text="Invitez. Partagez. Gagnez."
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-black leading-[1.05]"
            delay={0.2}
          />
          <p className="mt-6 text-base text-black/50 max-w-xl leading-relaxed">
            {data ? (
              <>
                Offrez à vos proches <strong className="text-black">-{data.discount_percent}%</strong> sur leur première commande.
                À chaque parrainage réussi, recevez <strong style={{ color: '#B80000' }}>{data.reward_per_referral_points.toLocaleString('fr-FR')} points fidélité</strong>.
              </>
            ) : 'Chargement de votre code...'}
          </p>
        </div>

        {data && (
          <>
            {/* Code + Share card */}
            <div className="border border-black/10 p-8 sm:p-12 mb-10" style={{ backgroundColor: '#FFFBF4' }}>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/50 mb-4">Votre code unique</p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div
                  data-testid="referral-code-box"
                  className="flex-1 p-5 font-mono text-xl sm:text-2xl tracking-[4px] font-bold text-center sm:text-left w-full"
                  style={{ color: '#B80000', backgroundColor: 'rgba(184,0,0,0.06)', border: '2px solid rgba(184,0,0,0.2)' }}
                >
                  {data.code}
                </div>
                <button
                  data-testid="copy-code-btn"
                  onClick={() => copy(data.code, 'Code')}
                  className="text-xs uppercase tracking-[0.2em] font-bold px-5 py-4 border border-black hover:bg-black hover:text-white transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                >
                  {copied === 'Code' ? <Check size={14} /> : <Copy size={14} />}
                  {copied === 'Code' ? 'Copié' : 'Copier'}
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-black/5">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/50 mb-3">Votre lien</p>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <code data-testid="referral-link" className="flex-1 p-3 bg-[#F5F0E8] text-xs text-black/70 truncate border border-black/5 w-full">{data.share_url}</code>
                  <button
                    data-testid="copy-link-btn"
                    onClick={() => copy(data.share_url, 'Lien')}
                    className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50 hover:text-black transition-colors flex items-center gap-2 flex-shrink-0"
                  >
                    {copied === 'Lien' ? <Check size={12} /> : <Copy size={12} />}
                    {copied === 'Lien' ? 'Copié' : 'Copier le lien'}
                  </button>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-black/5">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/50 mb-3 flex items-center gap-2">
                  <Share2 size={12} /> Partager directement
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'whatsapp', label: 'WhatsApp' },
                    { id: 'instagram', label: 'Instagram' },
                    { id: 'tiktok', label: 'TikTok' },
                    { id: 'facebook', label: 'Facebook' },
                    { id: 'x', label: 'X' },
                  ].map(s => (
                    <button
                      key={s.id}
                      data-testid={`share-${s.id}`}
                      onClick={() => share(s.id)}
                      className="text-xs uppercase tracking-[0.2em] font-bold px-4 py-2.5 border border-black/15 hover:border-[#B80000] hover:text-[#B80000] transition-colors"
                    >
                      {s.label}
                    </button>
                  ))}
                  {typeof navigator !== 'undefined' && navigator.share && (
                    <button
                      data-testid="share-native"
                      onClick={() => share('native')}
                      className="text-xs uppercase tracking-[0.2em] font-bold px-4 py-2.5 bg-black text-white hover:bg-[#B80000] transition-colors"
                    >
                      Plus d'options
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div data-testid="stat-redemptions" className="border border-black/10 p-6 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#B80000' }}>
                  <Users size={18} className="text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40">Filleuls</p>
                  <p className="font-serif text-3xl font-light mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {data.redemptions}
                  </p>
                </div>
              </div>
              <div data-testid="stat-points" className="border border-black/10 p-6 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0A0A0A' }}>
                  <Gift size={18} className="text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40">Points gagnés</p>
                  <p className="font-serif text-3xl font-light mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    <span style={{ color: '#B80000', fontWeight: 600 }}>{data.reward_points.toLocaleString('fr-FR')}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Top Ambassadrices Leaderboard */}
            {leaderboard.length > 0 && (
              <div data-testid="leaderboard-section" className="mt-12 border border-black/10 p-8 sm:p-10" style={{ backgroundColor: '#FFFBF4' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={14} style={{ color: '#B80000' }} />
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-black/50">Top Ambassadrices Balmart</p>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-light mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Les reines du parrainage
                </h3>
                <ol className="space-y-3">
                  {leaderboard.map((entry) => {
                    const isGold = entry.rank === 1;
                    const medal = entry.rank === 1 ? '#D4AF37' : entry.rank === 2 ? '#A8A8A8' : '#CD7F32';
                    return (
                      <li
                        key={entry.rank}
                        data-testid={`leaderboard-rank-${entry.rank}`}
                        className="flex items-center gap-4 p-4 border border-black/10 bg-white"
                      >
                        <div
                          className="w-10 h-10 flex items-center justify-center flex-shrink-0 font-serif text-lg font-semibold"
                          style={{ backgroundColor: medal, color: '#0A0A0A', fontFamily: 'Cormorant Garamond, serif' }}
                        >
                          {isGold ? <Crown size={16} strokeWidth={2} /> : entry.rank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{entry.display_name}</p>
                          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40 mt-0.5">
                            {entry.count} {entry.count > 1 ? 'filleules' : 'filleule'}
                          </p>
                        </div>
                        {isGold && (
                          <span
                            className="text-[9px] uppercase tracking-[0.3em] font-bold px-2 py-1"
                            style={{ backgroundColor: '#B80000', color: '#FFF' }}
                          >
                            #1
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
                <p className="text-xs text-black/40 mt-6 leading-relaxed">
                  Parrainez vos amies et gagnez votre place dans le top. À chaque filleule : <strong style={{ color: '#B80000' }}>{data.reward_per_referral_points.toLocaleString('fr-FR')} points</strong>.
                </p>
              </div>
            )}

            {/* How it works */}
            <div className="mt-16 border-t border-black/10 pt-12">
              <h3 className="font-serif text-2xl sm:text-3xl font-light mb-10" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Comment ça marche
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {[
                  { n: '01', t: 'Partagez votre code', d: 'Envoyez votre code ou votre lien à vos proches.' },
                  { n: '02', t: 'Ils commandent', d: `Vos amis reçoivent -${data.discount_percent}% sur leur première commande.` },
                  { n: '03', t: 'Vous êtes récompensée', d: `Vous gagnez ${data.reward_per_referral_points.toLocaleString('fr-FR')} points fidélité par filleul.` },
                ].map(s => (
                  <div key={s.n}>
                    <p className="text-4xl font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#B80000' }}>{s.n}</p>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold mb-2">{s.t}</p>
                    <p className="text-sm text-black/60 leading-relaxed">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
